import React, { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import Text from 'design-system/Text';
import Button from 'design-system/Button';
import Spacer from 'design-system/Spacer';
import CardRow from 'design-system/CardRow';
import { rem } from 'design-system/values';
import { CardView } from 'design-system/CardView';
import { IconEdit } from 'design-system/icons/IconEdit';
import useStyles from 'hooks/useStyles';
import Amplitude from 'utils/amplitude';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount } from 'utils/formatting';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { FREQUENCY, MONTHLY } from 'features/subscriptions/utils/frequency';
import getTransactionLogoUrl from 'features/transactions/utils/getTransactionLogoUrl';
import { getMinAndMax, getRangeString, } from 'features/subscriptions/utils/amounts';
import useColors from 'hooks/useColors';
import SubscriptionAd from './SubscriptionAd';
import SubscriptionLogo from '../SubscriptionLogo';
import PossibleDuplicateBanner from '../PossibleDuplicateBanner';
const SubscriptionHeader = ({ subscription, noMarginHorizontal, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const { currency, paymentFrequency, minMonthDay, maxMonthDay } = subscription;
    const amountRange = useMemo(() => {
        const symbolCurrency = currency || 'GBP';
        if (subscription.minAmount == null && subscription.maxAmount == null) {
            return (<Text Text-14 numberOfLines={1}>
          Any amount
        </Text>);
        }
        if (subscription.maxAmount == null && subscription.minAmount != null) {
            return (<Text Text-14 numberOfLines={1}>
          {'From '}
          <Text Numbers-14>
            {formatAmount(Math.floor(subscription.minAmount), symbolCurrency, 0)}
          </Text>
        </Text>);
        }
        if (subscription.minAmount == null && subscription.maxAmount != null) {
            return (<Text Text-14 numberOfLines={1}>
          {'Up to '}
          <Text Numbers-14>
            {formatAmount(Math.ceil(subscription.maxAmount), symbolCurrency, 0)}
          </Text>
        </Text>);
        }
        const [minAmount, maxAmount] = getMinAndMax(subscription);
        return (<Text Text-14 numberOfLines={1}>
        <Text Numbers-14>
          {formatAmount(Number(minAmount), symbolCurrency, 0)}
        </Text>
        {' to '}
        <Text Numbers-14>
          {formatAmount(Number(maxAmount), symbolCurrency, 0)}
        </Text>
      </Text>);
    }, [currency, subscription]);
    const frequencyLabel = paymentFrequency ? FREQUENCY[paymentFrequency] : '-';
    const showPaymentDate = paymentFrequency === MONTHLY && minMonthDay && maxMonthDay;
    const paymentDate = useMemo(() => {
        if (paymentFrequency === MONTHLY && minMonthDay && maxMonthDay) {
            return getRangeString(minMonthDay, maxMonthDay);
        }
        return 'Any day';
    }, [paymentFrequency, minMonthDay, maxMonthDay]);
    const onPress = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
        Amplitude.logEvent('Subscription.openCompare', {
            type: routeWithParams.name,
        });
    }, [navigation]);
    const onPressEdit = useCallback(() => {
        navigation.navigateDeprecated('AdjustSubscription', {
            id: subscription.id,
            edit: true,
        });
    }, [navigation, subscription]);
    if (!subscription.id) {
        return null;
    }
    const name = subscription.customName || subscription.merchantInfo?.displayName;
    return (<>
      <View style={styles.logoBox}>
        <SubscriptionLogo isHeader name={name} url={getTransactionLogoUrl(subscription)}/>
      </View>
      <Spacer.H8 />
      <PossibleDuplicateBanner subscriptionId={subscription.id} style={[
            styles.duplicateBanner,
            noMarginHorizontal ? styles.noMarginHorizontal : null,
        ]}/>
      <SubscriptionAd onCard onPress={onPress} merchant={subscription?.merchantInfo} noMarginHorizontal={noMarginHorizontal}/>
      <CardView noMarginBottom noMarginTop style={[
            noMarginHorizontal ? styles.noMarginHorizontal : null,
            styles.cardMargin,
        ]}>
        <Spacer.H4 />
        <CardRow title="Amount range" info={amountRange} tableSpacing small/>
        <CardRow title="Frequency" info={frequencyLabel} tableSpacing small/>
        {!!showPaymentDate && (<CardRow title="Payment date" info={paymentDate} tableSpacing small/>)}

        <View style={styles.buttonContainer}>
          <Button brandReversed title="Edit matching rules" onPress={onPressEdit} square leftIconContainerStyle={styles.buttonIcon} leftIcon={<IconEdit width={16} height={16} color={colors.text.textOnBrand}/>}/>
        </View>
      </CardView>
    </>);
};
const styleSet = createStyleSheets(() => ({
    logoBox: {
        alignItems: 'center',
    },
    bankAccount: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    buttonContainer: {
        padding: rem(16),
        paddingTop: rem(12),
    },
    noMarginHorizontal: {
        marginHorizontal: 0,
    },
    cardMargin: {
        marginTop: rem(16),
    },
    duplicateBanner: {
        marginTop: rem(16),
    },
    buttonIcon: {
        paddingRight: rem(8),
    },
}));
export default memo(SubscriptionHeader);
